'use client';
import Image from 'next/image';
import { motion } from 'motion/react';
import { Star } from 'lucide-react';
import GlobalTitle from '@shared/GlobalTitle';
import { platformReviews } from '@data/platformReviewsData';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: 'easeOut', delay: i * 0.1 },
  }),
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const CARD_VARIANTS = {
  light: {
    card: 'border-border/40 bg-card hover:shadow-lg hover:shadow-primary/5 hover:border-primary/30 hover:-translate-y-1',
    platform: 'text-ink',
    meta: 'text-muted-foreground',
  },
  dark: {
    card: 'border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/40 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
    platform: 'text-white',
    meta: 'text-white/60',
  },
};

function PlatformCard({ review, i, variant = 'light', size = 'md' }) {
  const hasRating = Boolean(review.rating);
  const v = CARD_VARIANTS[variant];
  const compact = size === 'sm';

  return (
    <motion.a
      href={review.href}
      target="_blank"
      rel="noopener noreferrer"
      custom={i}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUp}
      className={`group flex items-center gap-2.5 w-full h-full ${compact ? 'p-3' : 'p-4'} rounded-2xl border transition-all duration-300 ${v.card}`}
    >
      <div
        className={`relative ${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0 overflow-hidden transition-transform duration-300 group-hover:scale-110`}
        style={{ background: review.icon ? '#fff' : review.bg }}
      >
        {review.icon ? (
          <Image
            src={review.icon}
            alt={review.platform}
            fill
            sizes={compact ? '32px' : '40px'}
            draggable="false"
            className="object-contain p-1.5"
          />
        ) : (
          review.abbr
        )}
      </div>
      <div className="min-w-0">
        <p className={`font-bold truncate ${compact ? 'text-sm' : ''} ${v.platform}`}>{review.platform}</p>
        {hasRating ? (
          <div className="flex items-center gap-1 mt-0.5">
            <Star size={11} style={{ fill: review.starColor, color: review.starColor }} />
            <span className={`text-xs ${v.meta}`}>{review.rating} / 5{compact ? '' : ` · ${review.count}`}</span>
          </div>
        ) : (
          <p className={`text-xs mt-0.5 ${v.meta}`}>View profile</p>
        )}
      </div>
    </motion.a>
  );
}

/**
 * Just the grid of platform cards — embed anywhere without the title/section chrome.
 *
 * Uses flex-wrap (not CSS grid) on purpose: with `justify-center`, a leftover last row
 * — 3 cards after two full rows of 5, 1 after a full row of 2, etc. — centers itself
 * automatically, for any total card count, with no special-casing needed as platforms
 * are added or removed. `itemWidth` sets each card's width per breakpoint (as flex-basis
 * classes); the default lays out 1 col on mobile, 2 on tablet, 4 from `lg` up.
 */
export function PlatformReviewCards({
  variant = 'light',
  itemWidth = 'sm:w-[calc(50%-0.5rem)] lg:w-[calc(25%-0.75rem)]',
  size = 'md',
  className = '',
  limit,
}) {
  const reviews = limit ? platformReviews.slice(0, limit) : platformReviews;

  return (
    <div className={`flex flex-wrap justify-center gap-4 ${className}`}>
      {reviews.map((review, i) => (
        <div key={review.platform} className={`${itemWidth}`}>
          <PlatformCard review={review} i={i} variant={variant} size={size} />
        </div>
      ))}
    </div>
  );
}

export default function PlatformReviewsSection({ bgClass = 'bg-white', itemWidth, limit, className = '' }) {
  return (
    <section className={`py-12 md:py-20 overflow-hidden ${bgClass}`}>
      <div className="max-w-[1440px] mx-auto px-6">

        <div className="text-center mb-8">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={fadeUp}>
              <GlobalTitle
                topText="Verified By Clients"
                titleText="VelanApps Reviews Across"
                coloredText="Top Software Review Platforms"
                classes="!text-center items-center"
                topTextClass="justify-center"
              />
            </motion.div>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
              Independently verified ratings and client reviews of VelanApps on trusted third-party platforms — not just testimonials on our own site.
            </motion.p>
          </motion.div>
        </div>

        <PlatformReviewCards itemWidth={itemWidth} limit={limit} className={className} />
      </div>
    </section>
  );
}
